import { useCallback, useMemo } from 'react';
import { DeliveryBarMachineContext } from '@monofront/business/contexts';
import { useDeliveryBar } from '@context/DeliveryBarContext';

import {
  machineSelectedAddressSelector,
  machineSelectedShippingMethodSelector,
  machineShippingMethodsSelector,
} from '@monofront/business/stateMachines/deliveryBar/selectors';
import { useSerializers } from './useSerializers';
import {
  ISlotAvailabilitiesV1QueryResult,
  ShippingMethodTypes,
} from '@monofront/types';
import { GET_SLOT_AVAILABILITIES } from '@monofront/business/queries';
import { useQuery } from '@apollo/client';

export const useShippingMethodInformationPanel = () => {
  const { shippingMethodSelectionContent } = useDeliveryBar();
  const shippingMethods = DeliveryBarMachineContext.useSelector(
    machineShippingMethodsSelector,
  );

  const selectedAddress = DeliveryBarMachineContext.useSelector(
    machineSelectedAddressSelector,
  );

  const selectedShippingMethod = DeliveryBarMachineContext.useSelector(
    machineSelectedShippingMethodSelector,
  );

  const courrierMethod = useMemo(() => {
    if (selectedShippingMethod?.type === ShippingMethodTypes.COURIER) {
      return selectedShippingMethod;
    }

    return shippingMethods?.find(
      (el) => el.type === ShippingMethodTypes.COURIER,
    );
  }, [selectedShippingMethod, shippingMethods]);

  const { data: slotsData } = useQuery<ISlotAvailabilitiesV1QueryResult>(
    GET_SLOT_AVAILABILITIES,
    {
      variables: {
        countryCode: selectedAddress?.country,
        zipCode: selectedAddress?.postalCode,
        isFresh: false,
        isUltraFresh: false,
        carrierMethod: courrierMethod?.carrierId,
      },
      skip:
        !selectedAddress?.country ||
        !selectedAddress?.postalCode ||
        !courrierMethod?.carrierId,
    },
  );

  const { descriptionSerializer, subtitleSerializer } = useSerializers();

  const findEligibleShippingMethod = useCallback(
    (shippingMethodType: ShippingMethodTypes) => {
      return shippingMethods?.find((el) => el.type === shippingMethodType);
    },
    [shippingMethods],
  );

  return {
    shippingMethodSelectionContent,
    shippingMethods,
    slots: slotsData?.getSlotAvailabilities ?? [],
    descriptionSerializer,
    subtitleSerializer,
    findEligibleShippingMethod,
  };
};
